import { z } from 'zod';
import { attachmentKindSchema, attachmentStageSchema, departmentSchema, ticketPrioritySchema, ticketStatusSchema, } from '../enums.js';
import { TicketStatusGroup, ticketStatusGroupSchema } from '../lifecycle/status-groups.js';
import { propertyDateKeySchema } from './date-key.js';
/**
 * Header carrying a stable, client-generated device id.
 *
 * Not a credential — it identifies which browser profile is talking, so the server
 * can bound backdating by that device's own last successful sync (decision D18).
 */
export const DEVICE_ID_HEADER = 'x-nbh-device-id';
/**
 * How many photos one fault report may carry.
 *
 * Lives here rather than as a literal on each side because the two sides disagreeing is
 * expensive in one direction: an unbounded picker uploads every photo, the create then
 * fails Zod validation, and a server rejection is not `TypeError`, so the offline branch
 * does not fire and the whole report is lost. The picker stops at this number instead.
 */
export const MAX_TICKET_PHOTOS = 10;
/**
 * How many messages `GET /api/tickets/:id/messages` returns, at most — the newest.
 *
 * Shared so the thread can say "Showing the latest 200 messages" when it receives exactly
 * this many, rather than truncating silently. No cursor: at this property's volume a
 * thread this long is rare, and a message lost in an argument about who said what is
 * worse than a line admitting the cap.
 */
export const TICKET_THREAD_PAGE = 200;
/** Body of POST /api/tickets. */
export const createTicketBodySchema = z.object({
    title: z.string().trim().min(3, 'Describe the fault in a few words').max(160),
    description: z.string().trim().max(4000).optional(),
    priority: ticketPrioritySchema.default('MEDIUM'),
    department: departmentSchema,
    locationId: z.string().min(1, 'A location is required'),
    assetId: z.string().min(1).optional(),
    /**
     * Names who is to do the work, turning a report into a directed job (D34).
     *
     * Optional, and omitted by the report form every frontline user sees: a fault report
     * is unassigned by definition and reaches its department as claimable work. Supplying
     * it requires `mayAssignWork` — the server refuses otherwise rather than quietly
     * dropping it, because a dispatcher who is told nothing would believe the job had been
     * sent to someone.
     */
    assigneeId: z.string().min(1).optional(),
    /**
     * Attachment ids from a prior upload. Media is uploaded first and referenced here,
     * so a large photo never blocks the ticket itself from being created.
     */
    attachmentIds: z.array(z.string().min(1)).max(MAX_TICKET_PHOTOS).optional(),
    /**
     * When the reporter actually hit submit. Offline queued writes carry the original
     * moment so SLA deadlines are not measured from whenever the sync happened to land.
     */
    reportedAt: z.coerce.date().optional(),
});
/** Query for GET /api/tickets. */
/**
 * Which slice of history the feed covers.
 *
 * `all` is the default because the offline queue and every existing caller depend on
 * it. `today` is the shift-board view: the property's current calendar day, plus
 * anything older still unresolved — a leak reported on Friday is the day shift's
 * problem on Monday, and dropping it at midnight is how it gets forgotten.
 */
export const ticketScopeSchema = z.enum(['all', 'today']);
/**
 * The Closed archive's bounds are the only date filter the queue has, and they apply to
 * the Closed group alone. New, In Progress and To Verify show every matching job
 * regardless of age: an open job nobody has touched in eight days is the single most
 * important row on the screen, and a cutoff there would make the app agree with the
 * neglect. Refused rather than ignored, so a client that sends a range on a live tab
 * gets a 400 instead of a quietly narrowed list.
 *
 * Both bounds are inclusive calendar dates in property time (`propertyDateKeySchema`),
 * widened to a half-open range server-side. No cursor, no page: the range *is* the
 * bound, and `limit` is the safety net.
 */
const closedRangeRules = (value) => (value.from === undefined && value.to === undefined) ||
    (value.from !== undefined && value.to !== undefined && value.group === TicketStatusGroup.Closed);
export const listTicketsQuerySchema = z
    .object({
    status: ticketStatusSchema.optional(),
    /**
     * A lifecycle tab, resolved to its statuses through `TICKET_STATUS_GROUP_OF` — never
     * a second list. Exclusive with `status`: one names a tab, the other a row in it.
     */
    group: ticketStatusGroupSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    department: departmentSchema.optional(),
    /** Only tickets assigned to the caller. */
    mine: z.coerce.boolean().optional(),
    scope: ticketScopeSchema.default('all'),
    from: propertyDateKeySchema.optional(),
    to: propertyDateKeySchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .refine((value) => value.status === undefined || value.group === undefined, {
    message: 'Give a status or a group, not both',
    path: ['group'],
})
    .refine(closedRangeRules, {
    message: 'A date range needs both from and to, and applies to the closed group only',
    path: ['from'],
});
/** Body of POST /api/tickets/:id/messages. */
export const createMessageBodySchema = z
    .object({
    body: z.string().trim().max(2000).default(''),
    /** Hidden from EXTERNAL_VENDOR. Only internal staff may set this. */
    isInternal: z.boolean().default(false),
    attachmentIds: z.array(z.string().min(1)).max(5).optional(),
})
    .refine((value) => value.body.length > 0 || (value.attachmentIds?.length ?? 0) > 0, {
    message: 'A message needs text or an attachment',
    path: ['body'],
});
/**
 * Body of POST /api/tickets/:id/transition.
 *
 * ON_HOLD and its return leg are deliberately excluded: those move the SLA clock and
 * go through the dedicated hold/resume endpoints so the pause accounting cannot be
 * bypassed (decision D10).
 *
 * VERIFIED is on the wire because closure now passes through it — CLOSED is no longer
 * reachable from RESOLVED, so without this the machine's tail would be unreachable over
 * the API even though the shared guard permits it.
 */
export const transitionTicketBodySchema = z.object({
    to: z.enum(['IN_PROGRESS', 'RESOLVED', 'VERIFIED', 'CLOSED']),
    reason: z.string().trim().max(500).optional(),
    /**
     * An already-uploaded photo to mark as the close-out proof (D2, D33).
     *
     * An id rather than the file itself: media goes up through `POST /attachments`
     * first and this claims it, the same two-step the fault report already uses. That
     * keeps the transition a small JSON body the offline outbox can queue and replay,
     * which a multipart request could not be.
     *
     * Optional and always optional. §5.6 reports proof coverage as a quality metric,
     * never a gate — a technician standing in a plant room with a dead camera still
     * has to be able to close the job.
     */
    proofAttachmentId: z.string().optional(),
});
/**
 * The reason chips the reassignment sheet offers (D38).
 *
 * i18n leaves under `reassign.chip.*`, not stored strings. What lands in the trail is
 * whatever the manager saw and chose — a Spanish duty manager's row reads in Spanish —
 * because `reason` is free text rendered back verbatim, and storing an English code the
 * renderer would have to translate is how a trail acquires a lookup table that drifts.
 *
 * Shared so the list and its order live in one place; the copy lives in the locale files
 * and the parity test keeps `en` and `es` in step.
 *
 * The reason stays optional (R4). Reject made its reason mandatory (D7) because the
 * recipient cannot act on "rejected" alone, and a technician losing a job has the same
 * question — but a duty manager reshuffling six jobs at shift change has six reasons to
 * type, and a mandatory field there buys silence, not information. Chips make the common
 * answers one tap; submitting with nothing costs exactly the same number of taps.
 */
export const REASSIGN_REASONS = ['shiftChange', 'offSick', 'wrongDepartment', 'workload'];
/** The i18n key for one chip. */
export function reassignReasonLabelKey(reason) {
    return `reassign.chip.${reason}`;
}
/**
 * Body of POST /api/tickets/:id/assignee.
 *
 * One route for dispatch and reassignment alike, because it is one write (D38). The
 * server refuses a no-op — assigning the current assignee to their own job is a mistake
 * worth reporting, not a success worth faking.
 *
 * No status field and no department field. Status is the state machine's and reaches this
 * row through no other door; department follows the assignee automatically when the two
 * disagree, because the queue is how staff find work and a stale one hides it.
 */
export const assignTicketBodySchema = z.object({
    assigneeId: z.string().min(1, 'Choose who is to do the work'),
    /** Optional, and shown to the person who loses the job. Never required — see above. */
    reason: z.string().trim().max(500).optional(),
});
/**
 * Body of PATCH /api/tickets/:id/checklist/:stepId.
 *
 * `photoAttachmentId` is attached separately from `isCompleted` on purpose: a step
 * can be signed off before its photo has finished uploading, and the photo can land
 * afterwards (decision D12). Neither waits on the other.
 */
export const updateChecklistStepBodySchema = z
    .object({
    isCompleted: z.boolean().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    photoAttachmentId: z.string().min(1).nullable().optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Provide at least one of: isCompleted, notes, photoAttachmentId',
});
/** Ticket as returned to clients — a flattened view, not the raw row. */
export const ticketSummarySchema = z.object({
    id: z.string(),
    reference: z.number().int(),
    title: z.string(),
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
    locationName: z.string().nullable(),
    assigneeName: z.string().nullable(),
    createdAt: z.string(),
    slaAckDeadline: z.string(),
    slaResolveDeadline: z.string(),
    holdReason: z.string().nullable(),
    /**
     * Raw instants for the breach badges, deliberately not a server-computed state.
     * Queue rows sit cached in Dexie, where a shipped state enum goes stale; a client
     * computation from stored instants keeps ticking offline, which is the design's
     * own Rule 4. `currentHoldStartedAt` is the load-bearing one: without it a held
     * job reads "Fix overdue" for time a vendor is consuming, billing the team for a
     * wait D6 says must never be charged to them (`effectiveResolveDeadline` is the
     * correction, shared with the CSV export).
     */
    acknowledgedAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    currentHoldStartedAt: z.string().nullable(),
});
/** One checklist step as the detail screen renders it. */
export const ticketChecklistItemSchema = z.object({
    id: z.string(),
    stepNumber: z.number().int(),
    instruction: z.string(),
    isRequired: z.boolean(),
    requirePhoto: z.boolean(),
    isCompleted: z.boolean(),
    photoAttachmentId: z.string().nullable(),
    notes: z.string().nullable(),
});
/**
 * One piece of media hanging off the job, as the detail screen renders it.
 *
 * The gallery, not the chat thread: message attachments are excluded upstream and
 * travel on `TicketMessagePayload` instead. Mixing them here would render a photo
 * twice and file a chat snapshot under a lifecycle heading it never claimed.
 *
 * `url` rather than raw bytes, and the same `/api/attachments/:id` route the chat
 * already uses — one permission-scoped download path, not two.
 */
export const ticketAttachmentSchema = z.object({
    id: z.string(),
    url: z.string(),
    /**
     * When in the job's life this was captured — ISSUE, COMPLETION or VERIFICATION.
     *
     * Null is a real and common answer, not a gap to be filled: a checklist-step photo is
     * proof about one SOP step rather than about the job's before-and-after, and it stays
     * null deliberately. Renderers group by stage and must have somewhere to put these.
     *
     * Optional at every stage and never a gate (D2, D12, D33). A job whose array is empty,
     * or whose stages are all null, is an ordinary job.
     */
    stage: attachmentStageSchema.nullable(),
    /** What the media *is* — PHOTO or VOICE — a different axis from `stage`. */
    kind: attachmentKindSchema,
    /**
     * Flattened to a name like every other person on the wire, and nullable for the same
     * reason `claimedByName` is: a hard-deleted account (D25) nulls the uploader reference
     * while the media stays.
     */
    uploadedByName: z.string().nullable(),
    /**
     * When the technician took it, which offline may long precede `createdAt` — the phone
     * captures in a plant room and uploads when it finds signal. Null on media that never
     * carried a capture time.
     */
    capturedAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * Intrinsic pixel size, so the client can reserve layout space before the bytes
     * arrive. Null on voice notes, and on photos uploaded before the server started
     * measuring them — a renderer must degrade to an aspect-ratio guess rather than
     * assume these are present.
     */
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    mimeType: z.string(),
});
/**
 * One row of the append-only trail.
 *
 * `actorName` is flattened from the joined user rather than sent as an id: the trail is
 * read by a person asking who did this, and an id they cannot resolve answers nothing.
 * Null covers both a system action and an account since deleted.
 */
export const ticketAuditEntrySchema = z.object({
    id: z.string(),
    action: z.string(),
    fromStatus: ticketStatusSchema.nullable(),
    toStatus: ticketStatusSchema.nullable(),
    reason: z.string().nullable(),
    actorName: z.string().nullable(),
    /**
     * Who the job moved between, on an ASSIGN row (D38).
     *
     * Names rather than ids, like `actorName` above and for the same reason: the trail is
     * read by a person asking who this went to, and an id they cannot resolve answers
     * nothing.
     *
     * Null on every row that is not an assignment, and null on assignment rows written
     * before the columns existed — the information was never captured, and the renderer has
     * to say so rather than invent it. Null also covers an account since hard-deleted (D25),
     * which is the same rendering problem with a different cause.
     */
    fromAssigneeName: z.string().nullable(),
    toAssigneeName: z.string().nullable(),
    /**
     * The queue the job moved between, when the reassignment crossed a department.
     *
     * Both null whenever it did not, which is most of the time. Carried on the same row as
     * the assignee change because it is the same act: the person moved and the queue
     * followed.
     */
    fromDepartment: departmentSchema.nullable(),
    toDepartment: departmentSchema.nullable(),
    /**
     * True when the verifier was the same person who completed the work.
     *
     * Self-verification is permitted — on a small property the duty manager is sometimes
     * the only person on site, and a hard block would just strand jobs overnight — so the
     * trail records it rather than preventing it. On the wire because the reader of the
     * history is the one who needs to see that four-eyes review did not happen.
     */
    isSelfVerified: z.boolean(),
    createdAt: z.string(),
});
/**
 * One ticket in full, as `GET /api/tickets/:id` returns it.
 *
 * Flattened like `ticketSummarySchema`, and for the same reason: the endpoint used to
 * return the raw Prisma row, so the client's declared type described a shape the server
 * never sent. `assigneeName` was always undefined and an assigned job rendered as
 * "Nobody yet". Extending the summary keeps the queue and the detail screen agreeing on
 * the fields they share, and makes any future drift a typecheck failure.
 */
export const ticketDetailSchema = ticketSummarySchema.extend({
    description: z.string().nullable(),
    department: departmentSchema,
    /**
     * The assignee's id, alongside the `assigneeName` inherited from the summary.
     *
     * An id as well as a name because the reassignment picker has to leave the current
     * holder out of the list — the server refuses a no-op, and offering a choice that can
     * only produce an error is worse than not offering it. Matching on the name instead
     * would break on two people called Sam.
     *
     * No new exposure: this is the id of a person whose name the same payload already
     * carries, on a job the caller can already see.
     */
    assigneeId: z.string().nullable(),
    reporterName: z.string().nullable(),
    totalHoldDurationMins: z.number().int(),
    // acknowledgedAt and resolvedAt are inherited from the summary now (breach badges).
    closedAt: z.string().nullable(),
    /**
     * Who did what, and when.
     *
     * Names rather than ids, like `assigneeName` and `actorName` above: the question being
     * asked of this screen is "who did this", and an id nobody can resolve answers nothing.
     *
     * All nullable, and each stage is nullable independently. A stage that has not happened
     * yet has no actor and no timestamp; a name can also be null on its own, because a
     * hard-deleted account (D25) nulls the ticket's reference to it while the timestamp
     * stays. So a non-null `claimedAt` with a null `claimedByName` is a real state, and the
     * client has to render it as "claimed, by an account since removed" rather than assume
     * the pair travel together.
     */
    claimedByName: z.string().nullable(),
    claimedAt: z.string().nullable(),
    completedByName: z.string().nullable(),
    /**
     * When the technician marked the job done. Diverges from `resolvedAt` after a
     * rejection: rejecting clears the completion attribution but leaves `resolvedAt`
     * standing, so a rejected-and-not-yet-redone job has `resolvedAt` set and
     * `completedAt` null.
     */
    completedAt: z.string().nullable(),
    verifiedByName: z.string().nullable(),
    verifiedAt: z.string().nullable(),
    closedByName: z.string().nullable(),
    checklistItems: z.array(ticketChecklistItemSchema),
    /**
     * The job's media, oldest first.
     *
     * The join was already being paid for on every detail load and then discarded — the
     * server fetched `attachments` and the serialiser never read them, so no photo could
     * reach the screen no matter how many were attached. This is the half that was missing.
     *
     * Chat media is excluded: it belongs to the message thread and is rendered there.
     */
    attachments: z.array(ticketAttachmentSchema),
    auditLog: z.array(ticketAuditEntrySchema),
});
//# sourceMappingURL=ticket.js.map